import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import type { AuthCredentials, AuthState } from '@/models/auth/types';

interface LoginFormProps {
  credentials: AuthCredentials;
  showPassword: boolean;
  state: AuthState;
  onFieldChange: <K extends keyof AuthCredentials>(field: K, value: AuthCredentials[K]) => void;
  onShowPasswordToggle: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onLoginFocus: () => void;
  onLoginBlur: () => void;
}

export function LoginForm({
  credentials,
  showPassword,
  state,
  onFieldChange,
  onShowPasswordToggle,
  onSubmit,
  onLoginFocus,
  onLoginBlur,
}: LoginFormProps) {
  return (
    <form
      onSubmit={onSubmit}
      autoComplete='on'
      className='bg-card text-card-foreground rounded-2xl border border-border shadow-md p-7 space-y-5'
    >
      <div className='space-y-1.5'>
        <Label
          htmlFor='login'
          className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'
        >
          Логин
        </Label>
        <Input
          id='login'
          name='username'
          type='text'
          placeholder='ivanov.ii'
          value={credentials.login}
          autoComplete='username'
          onChange={(e) => onFieldChange('login', e.target.value)}
          onFocus={onLoginFocus}
          onBlur={onLoginBlur}
          required
          className='h-11 bg-background border-border transition-colors'
        />
      </div>

      <div className='space-y-1.5'>
        <Label
          htmlFor='password'
          className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'
        >
          Пароль
        </Label>
        <div className='relative'>
          <Input
            id='password'
            name='password'
            type={showPassword ? 'text' : 'password'}
            placeholder='••••••••'
            value={credentials.password}
            autoComplete='current-password'
            onChange={(e) => onFieldChange('password', e.target.value)}
            required
            className='h-11 pr-10 bg-background border-border transition-colors'
          />
          <button
            type='button'
            onClick={onShowPasswordToggle}
            className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors'
          >
            {showPassword ? <EyeOff className='size-4' /> : <Eye className='size-4' />}
          </button>
        </div>
      </div>

      <div className='flex items-center space-x-2'>
        <Checkbox
          id='remember'
          checked={credentials.rememberMe}
          onCheckedChange={(checked) => onFieldChange('rememberMe', checked === true)}
        />
        <Label
          htmlFor='remember'
          className='text-sm font-normal cursor-pointer text-muted-foreground'
        >
          Запомнить меня
        </Label>
      </div>

      {state.error && (
        <div className='p-3 text-sm text-red-500 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 rounded-lg'>
          {state.error}
        </div>
      )}

      <Button
        type='submit'
        disabled={state.isLoading}
        className='w-full h-12 text-base font-semibold'
        size='lg'
      >
        {state.isLoading ? 'Выполняется вход...' : 'Войти'}
      </Button>
    </form>
  );
}
